import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, ArrowUpRight } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { PRODUCTS } from "@/lib/products";
import { FALLBACK_IDENTITY } from "@/lib/content";
import { fetchSiteIdentity, useWP } from "@/lib/wp";

const NAV = [
  { to: "/emi-calculator", label: "EMI Calculator", tid: "nav-emi-calculator-link" },
  { to: "/loan-comparison", label: "Compare Loans", tid: "nav-compare-loans-link" },
  { to: "/blog", label: "Blog", tid: "nav-blog-link" },
  { to: "/about", label: "About", tid: "nav-about-link" },
  { to: "/contact", label: "Contact", tid: "nav-contact-link" },
];

export const Header = () => {
  const identity = useWP(fetchSiteIdentity, FALLBACK_IDENTITY, []);
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [loansOpen, setLoansOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setLoansOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 glass-header transition-[box-shadow,border-color] duration-300 ${
        scrolled ? "border-b border-sand shadow-[0_8px_30px_rgba(30,74,61,0.08)]" : "border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="header-logo-link" aria-label="Loan Salaah home">
          <Logo icon={identity.icon} name={identity.name} />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          <div
            className="relative"
            onMouseEnter={() => setLoansOpen(true)}
            onMouseLeave={() => setLoansOpen(false)}
          >
            <button
              data-testid="nav-loans-dropdown"
              className="flex items-center gap-1.5 text-sm font-medium text-charcoal/80 transition-colors hover:text-concordia"
              onClick={() => setLoansOpen((v) => !v)}
              aria-expanded={loansOpen}
            >
              Loans
              <ChevronDown className={`h-3.5 w-3.5 text-brass transition-transform duration-300 ${loansOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {loansOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute left-1/2 top-full w-[34rem] -translate-x-1/2 pt-4"
                  data-testid="nav-loans-panel"
                >
                  <div className="grid grid-cols-2 gap-1 rounded-2xl border border-sand bg-cream p-3 shadow-[0_24px_60px_rgba(30,74,61,0.16)]">
                    {PRODUCTS.map((p) => (
                      <Link
                        key={p.slug}
                        to={`/loans/${p.slug}`}
                        data-testid={`nav-loan-${p.slug}`}
                        className="group flex items-start gap-3 rounded-xl p-3 transition-colors hover:bg-white"
                      >
                        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-concordia/[0.07] text-concordia transition-colors group-hover:bg-concordia group-hover:text-ivory">
                          <p.icon className="h-4 w-4" />
                        </span>
                        <span>
                          <span className="flex items-center gap-1 text-sm font-semibold text-charcoal">
                            {p.name}
                            <ArrowUpRight className="h-3 w-3 text-brass opacity-0 transition-opacity group-hover:opacity-100" />
                          </span>
                          <span className="mt-0.5 block text-xs leading-snug text-slate">{p.tagline}</span>
                        </span>
                      </Link>
                    ))}
                  </div>
                  <Link
                    to="/card-finder"
                    data-testid="nav-card-finder"
                    className="mt-2 block rounded-xl border border-brass/30 bg-white p-3 text-center text-xs font-semibold text-concordia transition-colors hover:border-brass hover:text-brass-deep"
                  >
                    Not sure which card? Try the 30-second Card Finder →
                  </Link>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.tid}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors hover:text-concordia ${
                  isActive ? "text-concordia" : "text-charcoal/80"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <Link
              to="/account"
              data-testid="header-account-link"
              className="hidden items-center gap-2 rounded-full border border-sand px-4 py-2 text-sm font-semibold text-concordia transition-colors hover:border-concordia/50 hover:bg-white sm:flex"
            >
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-concordia text-[0.65rem] font-bold text-ivory">
                {user.name?.charAt(0)?.toUpperCase() || "L"}
              </span>
              Account
            </Link>
          ) : (
            <Link
              to="/login"
              data-testid="header-signin-link"
              className="hidden text-sm font-semibold text-concordia transition-colors hover:text-brass-deep sm:inline-block"
            >
              Sign In
            </Link>
          )}
          <Link
            to="/apply"
            data-testid="header-apply-button"
            className="hidden rounded-full bg-concordia px-6 py-2.5 text-sm font-semibold text-ivory transition-all duration-300 hover:-translate-y-0.5 hover:bg-concordia-deep hover:shadow-[0_10px_24px_rgba(30,74,61,0.3)] sm:inline-block"
          >
            Apply Now
          </Link>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-button"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-sand text-concordia lg:hidden"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 border-sand bg-ivory">
              <div className="mt-8 flex flex-col gap-1">
                <p className="mb-2 text-[0.6rem] font-semibold uppercase tracking-[0.3em] text-brass">Loans</p>
                {PRODUCTS.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/loans/${p.slug}`}
                    data-testid={`mobile-loan-${p.slug}`}
                    className="rounded-lg px-3 py-2.5 text-sm font-medium text-charcoal transition-colors hover:bg-white"
                    onClick={() => setMobileOpen(false)}
                  >
                    {p.name}
                  </Link>
                ))}
                <div className="my-3 h-px bg-sand" />
                {NAV.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    data-testid={`mobile-${item.tid}`}
                    className="rounded-lg px-3 py-2.5 text-sm font-medium text-charcoal transition-colors hover:bg-white"
                    onClick={() => setMobileOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
                {user ? (
                  <Link
                    to="/account"
                    data-testid="mobile-account-link"
                    className="rounded-lg px-3 py-2.5 text-sm font-semibold text-concordia transition-colors hover:bg-white"
                    onClick={() => setMobileOpen(false)}
                  >
                    My Account
                  </Link>
                ) : (
                  <Link
                    to="/login"
                    data-testid="mobile-signin-link"
                    className="rounded-lg px-3 py-2.5 text-sm font-semibold text-concordia transition-colors hover:bg-white"
                    onClick={() => setMobileOpen(false)}
                  >
                    Sign In
                  </Link>
                )}
                <Link
                  to="/apply"
                  data-testid="mobile-apply-button"
                  className="mt-4 rounded-full bg-concordia px-6 py-3 text-center text-sm font-semibold text-ivory"
                  onClick={() => setMobileOpen(false)}
                >
                  Apply Now
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
